import React from "react";
import Card, { CardProps } from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { EmtTypography } from "./EmtTypography";
import { EmtIcon } from "./EmtIcon";
import { EmtButton } from "./EmtButton";
import { EmtLoader } from "./EmtLoader";
import { EmtRating } from "./EmtRating";
import { EmtChip } from "./EmtChip";
import { EmtCardLoader } from "./EmtCardLoader";

import { EmtTheme } from "../types";

export type EmtCardProps = Omit<CardProps, "variant"> & {
  titleNode?: React.ReactNode;
  distance?: string;
  button?: React.ReactNode;
  buttonTitle?: string;
  rating?: number | null;
  ratingLabel?: string;
  img?: React.ReactNode;
  online?: boolean;
  liked?: boolean | null;
  showLiked?: boolean | null;
  likeLoader?: boolean | null;
  isNew?: boolean;
  lastRefresh?: string;
  isNewLabel?: string;
  certified?: boolean;
  certifiedLabel?: string;
  premium?: boolean;
  premiumLabel?: string;
  showRating?: boolean;
  showButton?: boolean;
  onButtonClick?: () => void;
  onLikeClick?: (liked: boolean) => void | Promise<void>;
  onImageClick?: () => void;
  loader?: boolean;
  nonce?: string;
  variant?: any;
};

// -------------------- Updated Styled Components --------------------
// The StyledCard and StyledInlineCard now accept a "disableNameHover" prop.
// When true, the ":hover a" rule will not change the anchor's color.
const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "disableNameHover",
})<{ disableNameHover?: boolean }>(({ theme, disableNameHover }) => ({
  borderRadius: "18px",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  "& img": { width: "100%", borderRadius: "18px" },
  "& .MuiAvatar-root": {
    width: "100%",
    height: "auto",
    aspectRatio: "1",
  },
  "&:hover": {
    boxShadow: "0px 0px 5px 1px rgba(83,83,83,0.10)",
  },
  "& a:hover a": {
    color: disableNameHover ? "inherit" : theme?.palette.primary.main,
  },
  // "&:hover .titleNode": {
  //   color: disableNameHover ? "inherit" : theme?.palette.primary.main,
  // },
  "& a": {
    textDecoration: "none",
  },
}));

const StyledInlineCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "disableNameHover",
})<{ disableNameHover?: boolean }>(({ theme, disableNameHover }) => ({
  borderRadius: "18px",
  "& img": { width: "100%", borderRadius: "18px" },
  "& .MuiAvatar-root": {
    width: "100%",
    height: "auto",
    aspectRatio: "1",
  },
  "&:hover": {
    boxShadow: "0px 0px 5px 1px rgba(83,83,83,0.10)",
  },
  "&:hover a": {
    color: disableNameHover ? "inherit" : theme?.palette.primary.main,
  },
  "& a": {
    textDecoration: "none",
  },
}));

const StyledCardContent = styled(CardContent)(
  ({ theme }: { theme?: EmtTheme }) => ({
    padding: "8px 12px",
    paddingBottom: "12px !important",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    justifyContent: "space-between",
    "& a": {
      "&:hover": {
        color: theme?.palette.primary.main,
      },
      color: theme?.palette.greyGradient.main,
      textDecoration: "none",
    },
    [theme?.breakpoints?.down("md")]: {
      padding: "8px 8px",
    },
  })
);

const StyledInlineCardContent = styled(CardContent)(
  ({ theme }: { theme?: EmtTheme }) => ({
    padding: "8px 12px",
    paddingBottom: "12px !important",
    "& a": {
      "&:hover": {
        color: theme?.palette.primary.main,
      },
      color: theme?.palette.greyGradient.main,
      textDecoration: "none",
    },
    [theme?.breakpoints.down("md")]: {
      padding: "8px 8px",
    },
  })
);

const ChildrenContent = styled(Box)(({ theme }: { theme?: EmtTheme }) => ({
  color: theme?.palette.greyGradient.light,
  display: "flex",
  flexDirection: "column",
  padding: "3px 3px",
  "& .MuiTypography-root": {
    lineHeight: 1.3,
  },
}));

const HeartIcon = styled(EmtIcon)(
  ({ theme, className }: { theme?: EmtTheme; className: string }) => ({
    fontSize: "18px",
    cursor: "pointer",
    color: className.includes("active")
      ? theme?.palette.primary.main
      : theme?.palette.greyGradient.light,
    "&:hover": { color: theme?.palette.primary.main },
  })
);

const OnlineIcon = styled("span")(({ theme }: { theme?: EmtTheme }) => ({
  width: "8px",
  height: "8px",
  background: theme?.palette.success[550],
  borderRadius: "100%",
  display: "inline-block",
  marginLeft: "5px",
}));

const CertifiedPremiumBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  paddingTop: "3px",
  minHeight: "31px",
  gap: "3px",
  [theme.breakpoints.down("md")]: {
    height: "auto",
  },
}));

const StyledEmtChip = styled(EmtChip)(({ theme }) => ({
  fontSize: "12px",
  "& .MuiChip-label": { padding: "0 8px" },
  [theme.breakpoints.down("md")]: {
    "& .MuiChip-label": { padding: "0 7px" },
  },
}));

// -------------------- DefaultCard --------------------
function DefaultCard(props: EmtCardProps) {
  const {
    titleNode,
    distance = "0 km",
    rating = null,
    img,
    elevation = 0,
    children,
    certified = false,
    premium = false,
    certifiedLabel = "Certified",
    premiumLabel = "Premium",
    showRating = true,
    onButtonClick,
    onLikeClick,
    isNew = false,
    isNewLabel = "New",
    liked = false,
    online = false,
    ratingLabel,
    buttonTitle = "Contact",
    showButton = true,
    showLiked = true,
    likeLoader = false,
    ...rest
  } = props;

  // State to control whether the name's hover effect should be disabled.
  const [disableNameHover, setDisableNameHover] = React.useState(false);

  // When heart is clicked, call onLikeClick with the toggled value.
  const internalOnClick = React.useCallback(() => {
    if (onLikeClick) {
      onLikeClick(!liked);
    }
  }, [onLikeClick, liked]);

  // Build the heart node and wrap it in a container that stops propagation
  // and sets disableNameHover on mouse enter.
  const heartNode = likeLoader ? (
    <EmtLoader size={22} />
  ) : showLiked ? (
    <HeartIcon
      className={liked ? "emt-heart active" : "emt-heart-1"}
      onClick={internalOnClick}
    />
  ) : null;

  const heartNodeWrapped = heartNode ? (
    <div
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setDisableNameHover(true)}
      onMouseLeave={() => setDisableNameHover(false)}
    >
      {heartNode}
    </div>
  ) : null;

  return (
    <StyledCard
      disableNameHover={disableNameHover}
      {...rest}
      elevation={elevation}
    >
      <Box sx={{ position: "relative" }}>
        <>{img}</>
        {isNew && (
          <EmtChip
            label={isNewLabel}
            color="transparent"
            sx={{
              position: "absolute",
              top: "10px",
              left: "10px",
              fontWeight: "500",
            }}
          />
        )}
      </Box>
      <StyledCardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <EmtTypography variant="body1" fontWeight="bold" noWrap>
            {titleNode}
            {online && <OnlineIcon />}
          </EmtTypography>
          {heartNodeWrapped}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            paddingTop: "8px",
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "baseline", fontWeight: 600 }}
          >
            <EmtIcon
              className="emt-pin-1"
              sx={{ color: "greyGradient.light", fontWeight: 600 }}
              fontSize="small"
            />
            <EmtTypography variant="caption" color="greyGradient.light">
              {distance}
            </EmtTypography>
          </Box>
          {showRating && (
            <EmtRating
              defaultValue={rating ? rating : undefined}
              label={rating ? rating.toString() : undefined}
              labelFontSize="caption"
              labelFontColor="greyGradient.light"
              max={1}
              readOnly
              size="small"
              boxSx={{ display: "flex", alignItems: "baseline" }}
            />
          )}
        </Box>

        {children && <ChildrenContent>{children}</ChildrenContent>}

        <CertifiedPremiumBox>
          {certified && (
            <StyledEmtChip label={certifiedLabel} color="successlight" />
          )}
          {premium && (
            <StyledEmtChip
              label={premiumLabel}
              color="secondarylight"
              icon={<EmtIcon className="emt-crown" sx={{ fontSize: "12px" }} />}
            />
          )}
        </CertifiedPremiumBox>
        {showButton && (
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => setDisableNameHover(true)}
            onMouseLeave={() => setDisableNameHover(false)}
          >
            <EmtButton
              variant="outlined"
              fullWidth
              sx={{ marginTop: "14px", padding: "3px", borderRadius: "14px" }}
              onClick={onButtonClick}
            >
              {buttonTitle}
            </EmtButton>
          </div>
        )}
      </StyledCardContent>
    </StyledCard>
  );
}

// -------------------- InlineCard --------------------
function InlineCard(props: EmtCardProps) {
  const {
    titleNode,
    distance = "0 km",
    rating = null,
    img,
    elevation = 0,
    children,
    certified = false,
    premium = false,
    certifiedLabel = "Certified",
    premiumLabel = "Premium",
    showRating = true,
    onButtonClick,
    onLikeClick,
    onImageClick,
    isNew = false,
    isNewLabel = "New",
    liked = false,
    online = false,
    ratingLabel,
    buttonTitle = "Contact",
    showButton = true,
    showLiked = true,
    likeLoader = false,
    sx = {},
    ...rest
  } = props;

  const [disableNameHover, setDisableNameHover] = React.useState(false);

  const internalOnClick = React.useCallback(() => {
    if (onLikeClick) {
      onLikeClick(!liked);
    }
  }, [onLikeClick, liked]);

  const heartNode = likeLoader ? (
    <EmtLoader size={22} />
  ) : showLiked ? (
    <HeartIcon
      className={liked ? "emt-heart active" : "emt-heart-1"}
      onClick={internalOnClick}
    />
  ) : null;

  const heartNodeWrapped = heartNode ? (
    <div
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setDisableNameHover(true)}
      onMouseLeave={() => setDisableNameHover(false)}
    >
      {heartNode}
    </div>
  ) : null;

  return (
    <StyledInlineCard
      disableNameHover={disableNameHover}
      {...rest}
      elevation={elevation}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {img && (
          <Box
            className="EmtInlineCard-image-wrapper"
            sx={{
              maxWidth: "110px",
              maxHeight: "110px",
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              paddingLeft: "10px",
            }}
            onClick={onImageClick}
          >
            <>{img}</>
          </Box>
        )}
        <StyledInlineCardContent
          sx={{ flexGrow: 1, paddingBottom: "7px !important" }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <EmtTypography
              variant="body1"
              fontWeight="bold"
              noWrap
              sx={{ textOverflow: "ellipsis", maxWidth: "120px" }}
            >
              {titleNode}
              {online && <OnlineIcon />}
            </EmtTypography>
            {heartNodeWrapped}
          </Box>
          <Box sx={{ display: "flex", paddingTop: "2px" }}>
            <Box sx={{ display: "flex", alignItems: "baseline" }}>
              <EmtIcon
                className="emt-pin-1"
                sx={{ color: "greyGradient.light", fontWeight: 600 }}
                fontSize="small"
              />
              <EmtTypography
                variant="caption"
                color="greyGradient.light"
                sx={{ marginRight: "5px" }}
                fontWeight={600}
              >
                {distance}
              </EmtTypography>
            </Box>
            {showRating && (
              <>
                <EmtRating
                  defaultValue={rating ? rating : undefined}
                  label={
                    rating
                      ? `${rating.toString()} ${
                          ratingLabel ? `(${ratingLabel})` : ""
                        }`
                      : undefined
                  }
                  labelFontSize="caption"
                  labelFontColor="greyGradient.light"
                  max={1}
                  readOnly
                  size="small"
                  boxSx={{ display: "flex", alignItems: "baseline" }}
                />
              </>
            )}
          </Box>
          {(certified || premium) && (
            <CertifiedPremiumBox>
              {certified && (
                <EmtChip
                  label={certifiedLabel}
                  color="successlight"
                  size="small"
                />
              )}
              {premium && (
                <EmtChip
                  label={premiumLabel}
                  color="secondarylight"
                  size="small"
                  icon={<EmtIcon className="emt-crown" />}
                />
              )}
            </CertifiedPremiumBox>
          )}
          {showButton && (
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseEnter={() => setDisableNameHover(true)}
              onMouseLeave={() => setDisableNameHover(false)}
            >
              <EmtButton
                variant="outlined"
                size="small"
                sx={{
                  marginTop: "5px",
                  padding: "4px 12px",
                  borderRadius: "12px",
                }}
                onClick={onButtonClick}
              >
                {buttonTitle}
              </EmtButton>
            </div>
          )}
        </StyledInlineCardContent>
      </Box>

      {children}
    </StyledInlineCard>
  );
}

// -------------------- EmtCard Wrapper --------------------
export function EmtCard(props: EmtCardProps) {
  const { loader, ...rest } = props;
  return props.variant === "inline" || props.variant === "inlineOutlined" ? (
    loader ? (
      <EmtCardLoader variant="inline" />
    ) : (
      <InlineCard {...rest} />
    )
  ) : loader ? (
    <EmtCardLoader />
  ) : (
    <DefaultCard {...rest} />
  );
}
